// imported - small changes: 'extends UseFormValues'
import React from 'react';

type UseFormValues = {
  [field: string]: any;
};

type UseFormTouched<Values> = {
  [K in keyof Values]?: boolean;
};

type UseFormErrors<Values> = {
  [K in keyof Values]?: string;
};

type UseFormState<Values extends UseFormValues> = {
  values: Values;
  touched: UseFormTouched<Values>;
  errors: UseFormErrors<Values>;
  submitting: boolean;
};

type UseFormProps<Values> = {
  initialValues: Values;
  onSubmit: (values: Values) => void | Promise<any>;
  validate?: (values: Values) => UseFormErrors<Values>;
};

type HandleChange = (event: React.ChangeEvent<any>) => void;
type HandleBlur = (event: React.FocusEvent<any>) => void;
type HandleSubmit = (event: React.FormEvent<HTMLFormElement>) => void;
type GetFieldProps<Values> = (name: keyof Values) => {
  name: keyof Values;
  value: any;
  onChange: HandleChange;
  onBlur: HandleBlur;
  touched?: boolean;
  error?: string;
};

type UseFormResponse<Values> = {
  values: Values;
  touched: UseFormTouched<Values>;
  errors: UseFormErrors<Values>;
  submitting: boolean;
  handleChange: HandleChange;
  handleBlur: HandleBlur;
  handleSubmit: HandleSubmit;
  getFieldProps: GetFieldProps<Values>;
};

type SetFieldValueAction = {
  type: 'SET_FIELD_VALUE';
  payload: { [field: string]: string };
};

type SetFieldTouchedActions = {
  type: 'SET_FIELD_TOUCHED';
  payload: { [field: string]: boolean };
};

type SetErrorsAction<Values> = {
  type: 'SET_ERRORS';
  payload: UseFormErrors<Values>;
};

type SubmitAttemptAction = {
  type: 'SUBMIT_ATTEMPT';
};

type SubmitSuccessAction = {
  type: 'SUBMIT_SUCCESS';
};

type SubmitFailedAction = {
  type: 'SUBMIT_FAILED';
};

type UseFormActions<Values> =
  | SetFieldValueAction
  | SetFieldTouchedActions
  | SetErrorsAction<Values>
  | SubmitAttemptAction
  | SubmitSuccessAction
  | SubmitFailedAction;

export function useFormReducer<Values extends UseFormValues>(
  state: UseFormState<Values>,
  action: UseFormActions<Values>
): UseFormState<Values> {
  switch (action.type) {
    case 'SET_FIELD_VALUE':
      return {
        ...state,
        values: {
          ...state.values,
          ...action.payload
        }
      };

    case 'SET_FIELD_TOUCHED':
      return {
        ...state,
        touched: {
          ...state.touched,
          ...action.payload
        }
      };

    case 'SET_ERRORS':
      return {
        ...state,
        errors: action.payload
      };

    case 'SUBMIT_ATTEMPT':
      return {
        ...state,
        submitting: true,
        touched: Object.keys(state.values).reduce((acc, key) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          acc[key] = true;
          return acc;
        }, {})
      };

    case 'SUBMIT_SUCCESS':
      return {
        ...state,
        submitting: false
      };

    case 'SUBMIT_FAILED':
      return {
        ...state,
        submitting: false
      };

    default:
      return state;
  }
}

const useForm = <Values extends UseFormValues>(
  props: UseFormProps<Values>
): UseFormResponse<Values> => {
  const initialState = {
    values: props.initialValues,
    touched: {},
    errors: {},
    submitting: false
  };

  const [state, dispatch] = React.useReducer<
    React.Reducer<UseFormState<Values>, UseFormActions<Values>>
  >(useFormReducer, initialState);

  const validateForm = React.useCallback(
    (values: Values) => {
      if (props.validate) {
        const errors = props.validate(values);

        dispatch({
          type: 'SET_ERRORS',
          payload: errors
        });
      }
    },
    [props]
  );

  const handleChange = React.useCallback((event: React.ChangeEvent<any>) => {
    event.persist();

    const { type, checked, value } = event.target;

    const nextValue = type === 'checkbox' ? checked : value;

    dispatch({
      type: 'SET_FIELD_VALUE',
      payload: { [event.target.name]: nextValue }
    });
  }, []);

  const handleBlur = React.useCallback((event: React.FocusEvent<any>) => {
    event.persist();

    dispatch({
      type: 'SET_FIELD_TOUCHED',
      payload: { [event.target.name]: true }
    });
  }, []);

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      dispatch({ type: 'SUBMIT_ATTEMPT' });

      validateForm(state.values);

      if (Object.keys(state.errors).length) {
        return dispatch({ type: 'SUBMIT_FAILED' });
      }

      try {
        await props.onSubmit(state.values);
        dispatch({ type: 'SUBMIT_SUCCESS' });
      } catch (_) {
        dispatch({ type: 'SUBMIT_FAILED' });
      }
    },
    [props, state.errors, state.values, validateForm]
  );

  const getFieldProps = React.useCallback(
    (name: keyof Values) => {
      return {
        name,
        value: typeof state.values[name] !== 'boolean' ? state.values[name] : undefined,
        error: state.errors[name],
        touched: state.touched[name],
        onChange: handleChange,
        onBlur: handleBlur,
        checked: typeof state.values[name] === 'boolean' ? state.values[name] : undefined
      };
    },
    [state.errors, state.values, state.touched, handleBlur, handleChange]
  );

  React.useEffect(() => {
    validateForm(state.values);
  }, [state.values]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...state, handleChange, handleBlur, handleSubmit, getFieldProps };
};

export default useForm;
